/**
 * 결정론 수학 — 어느 JS 엔진에서도 비트 단위로 같은 sin·cos·atan·atan2·acos·hypot (CRQ-001 R-4 · SR-PHY-03).
 *
 * 원본: picnic-field `poc-final:poc/p1-kinematics/src/det-math.js`(PoC P1) — 산식·상수·연산 순서를 **한 글자도 바꾸지 않고**
 * ES 모듈 + 타입으로 옮겼다. 전역 Math 를 갈아 끼우던 `install()` 은 빼고, K 파일이 직접 import 한다.
 *
 * 왜: 엔진의 초월함수는 1 ULP 안에서 제각각이다(Node 25 ↔ Chromium 153 에서 sin·cos·acos·atan2 입력의 3~18%).
 * 어떻게: musl libm(fdlibm 계열) 알고리즘을 **사칙연산과 Math.sqrt 만으로** 옮겼다 — IEEE 754 가 결과 비트를 정한다.
 * 범위: sin·cos 인수 축소는 3단 Cody–Waite 라 |x| < 2^20·π/2 ≈ 1.6e6 rad 에서 정확하다(모터 10만 바퀴 = 6.3e5 rad).
 */

/* eslint-disable no-loss-of-precision, no-useless-assignment -- musl 상수 표기·인수 축소 구조를 원본 그대로 둔다 */

const buf = new DataView(new ArrayBuffer(8));
/** 하위 32비트를 0 으로(musl 의 SET_LOW_WORD(df, 0)) — 끝 순서를 명시해 엔진·CPU 와 무관하게 */
const clearLow = (x: number): number => {
  buf.setFloat64(0, x, false);
  buf.setUint32(4, 0, false);
  return buf.getFloat64(0, false);
};

// ---- sin / cos 커널 (musl __sin.c · __cos.c) ----
const S1 = -1.66666666666666324348e-1,
  S2 = 8.33333333332248946124e-3,
  S3 = -1.98412698298579493134e-4,
  S4 = 2.75573137070700676789e-6,
  S5 = -2.50507602534068634195e-8,
  S6 = 1.58969099521155010221e-10;
const C1 = 4.16666666666666019037e-2,
  C2 = -1.38888888888741095749e-3,
  C3 = 2.48015872894767294178e-5,
  C4 = -2.75573143513906633035e-7,
  C5 = 2.0875723212981748279e-9,
  C6 = -1.13596475577881948265e-11;
const kSin = (x: number, y: number, iy: number): number => {
  const z = x * x,
    w = z * z;
  const r = S2 + z * (S3 + z * S4) + z * w * (S5 + z * S6);
  const v = z * x;
  return iy === 0 ? x + v * (S1 + z * r) : x - (z * (0.5 * y - v * r) - y - v * S1);
};
const kCos = (x: number, y: number): number => {
  const z = x * x,
    w = z * z;
  const r = z * (C1 + z * (C2 + z * C3)) + w * w * (C4 + z * (C5 + z * C6));
  const hz = 0.5 * z,
    t = 1.0 - hz;
  return t + (1.0 - t - hz + (z * r - x * y));
};

// ---- 인수 축소 (musl __rem_pio2.c 중간 범위, 3단 고정) ----
const invpio2 = 6.36619772367581382433e-1,
  pio2_1 = 1.57079632673412561417,
  pio2_1t = 6.07710050650619224932e-11,
  pio2_2 = 6.0771005063039659766e-11,
  pio2_2t = 2.02226624879595063154e-21,
  pio2_3 = 2.0222662487111664558e-21,
  pio2_3t = 8.47842766036889956997e-32;
const PIO4 = 7.85398163397448278999e-1;
const remPio2 = (x: number): [number, number, number] => {
  const fn = Math.round(x * invpio2);
  let r = x - fn * pio2_1,
    w = fn * pio2_1t,
    t: number;
  t = r;
  w = fn * pio2_2;
  r = t - w;
  w = fn * pio2_2t - (t - r - w);
  t = r;
  w = fn * pio2_3;
  r = t - w;
  w = fn * pio2_3t - (t - r - w);
  const y0 = r - w,
    y1 = r - y0 - w;
  return [((fn % 4) + 4) % 4, y0, y1];
};

export const sin = (x: number): number => {
  if (!(x === x) || x === Infinity || x === -Infinity) return NaN;
  if (Math.abs(x) < PIO4) return kSin(x, 0, 0);
  const [n, y0, y1] = remPio2(x);
  return n === 0
    ? kSin(y0, y1, 1)
    : n === 1
      ? kCos(y0, y1)
      : n === 2
        ? -kSin(y0, y1, 1)
        : -kCos(y0, y1);
};

export const cos = (x: number): number => {
  if (!(x === x) || x === Infinity || x === -Infinity) return NaN;
  if (Math.abs(x) < PIO4) return kCos(x, 0);
  const [n, y0, y1] = remPio2(x);
  return n === 0
    ? kCos(y0, y1)
    : n === 1
      ? -kSin(y0, y1, 1)
      : n === 2
        ? -kCos(y0, y1)
        : kSin(y0, y1, 1);
};

// ---- atan / atan2 (musl atan.c · atan2.c) ----
const atanhi = [
  4.63647609000806093515e-1, 7.85398163397448278999e-1, 9.82793723247329054082e-1,
  1.570796326794896558,
] as const;
const atanlo = [
  2.26987774529616870924e-17, 3.06161699786838301793e-17, 1.39033110312309984516e-17,
  6.12323399573676603587e-17,
] as const;
const aT = [
  3.33333333333329318027e-1, -1.99999999998764832476e-1, 1.42857142725034663711e-1,
  -1.1111110405462355788e-1, 9.09088713343650656196e-2, -7.69187620504482999495e-2,
  6.66107313738753120669e-2, -5.83357013379057348645e-2, 4.97687799461593236017e-2,
  -3.6531572744216915527e-2, 1.62858201153657823623e-2,
] as const;

export const atan = (x0: number): number => {
  if (!(x0 === x0)) return NaN;
  const neg = x0 < 0 || Object.is(x0, -0);
  let x = Math.abs(x0);
  let id: 0 | 1 | 2 | 3 | -1;
  if (x >= 7.378697629483821e19) return neg ? -atanhi[3] - atanlo[3] : atanhi[3] + atanlo[3]; // 2^66
  if (x < 0.4375) {
    if (x < 7.450580596923828e-9) return x0; // 2^-27
    id = -1;
  } else if (x < 1.1875) {
    if (x < 0.6875) {
      id = 0;
      x = (2.0 * x - 1.0) / (2.0 + x);
    } else {
      id = 1;
      x = (x - 1.0) / (x + 1.0);
    }
  } else if (x < 2.4375) {
    id = 2;
    x = (x - 1.5) / (1.0 + 1.5 * x);
  } else {
    id = 3;
    x = -1.0 / x;
  }
  const z = x * x,
    w = z * z;
  const s1 = z * (aT[0] + w * (aT[2] + w * (aT[4] + w * (aT[6] + w * (aT[8] + w * aT[10])))));
  const s2 = w * (aT[1] + w * (aT[3] + w * (aT[5] + w * (aT[7] + w * aT[9]))));
  if (id < 0) {
    const r = x - x * (s1 + s2);
    return neg ? -r : r;
  }
  const k = id as 0 | 1 | 2 | 3;
  const r = atanhi[k] - (x * (s1 + s2) - atanlo[k] - x);
  return neg ? -r : r;
};

const PI = 3.141592653589793116,
  PI_LO = 1.2246467991473531772e-16;
const negZ = (v: number): boolean => v < 0 || Object.is(v, -0);
const INF_BOTH = [PI / 4, -PI / 4, (3 * PI) / 4, (-3 * PI) / 4] as const;
const INF_X = [0, -0, PI, -PI] as const;

export const atan2 = (y: number, x: number): number => {
  if (!(x === x) || !(y === y)) return NaN;
  if (x === 1.0) return atan(y);
  const m = ((negZ(y) ? 1 : 0) | (negZ(x) ? 2 : 0)) as 0 | 1 | 2 | 3;
  if (y === 0) return m === 0 || m === 1 ? y : m === 2 ? PI : -PI;
  if (x === 0) return m & 1 ? -PI / 2 : PI / 2;
  if (x === Infinity || x === -Infinity) {
    if (y === Infinity || y === -Infinity) return INF_BOTH[m];
    return INF_X[m];
  }
  if (y === Infinity || y === -Infinity) return m & 1 ? -PI / 2 : PI / 2;
  const q = Math.abs(y / x);
  const z = m & 2 && q < 8.673617379884035e-19 ? 0 : atan(q); // 2^-60
  return m === 0 ? z : m === 1 ? -z : m === 2 ? PI - (z - PI_LO) : z - PI_LO - PI;
};

// ---- acos (musl acos.c) ----
const pS0 = 1.66666666666666657415e-1,
  pS1 = -3.25565818622400915405e-1,
  pS2 = 2.01212532134862925881e-1,
  pS3 = -4.00555345006794114027e-2,
  pS4 = 7.91534994289814532176e-4,
  pS5 = 3.4793310759602116757e-5,
  qS1 = -2.40339491173441421878,
  qS2 = 2.02094576023350569471,
  qS3 = -6.8828397160545329303e-1,
  qS4 = 7.70381505559019352791e-2;
const PIO2_HI = 1.570796326794896558,
  PIO2_LO = 6.12323399573676603587e-17;
const R = (z: number): number => {
  const p = z * (pS0 + z * (pS1 + z * (pS2 + z * (pS3 + z * (pS4 + z * pS5)))));
  const q = 1.0 + z * (qS1 + z * (qS2 + z * (qS3 + z * qS4)));
  return p / q;
};

export const acos = (x: number): number => {
  if (!(x === x)) return NaN;
  const ax = Math.abs(x);
  if (ax >= 1) return x === 1 ? 0 : x === -1 ? 2 * PIO2_HI + 7.52316384526264005099e-37 : NaN;
  if (ax < 0.5) {
    if (ax <= 6.938893903907228e-18) return PIO2_HI + 7.52316384526264005099e-37; // 2^-57
    return PIO2_HI - (x - (PIO2_LO - x * R(x * x)));
  }
  if (x < 0) {
    const z = (1.0 + x) * 0.5,
      s = Math.sqrt(z),
      w = R(z) * s - PIO2_LO;
    return 2 * (PIO2_HI - (s + w));
  }
  const z = (1.0 - x) * 0.5,
    s = Math.sqrt(z),
    df = clearLow(s);
  const c = (z - df * df) / (s + df),
    w = R(z) * s + c;
  return 2 * (df + w);
};

/** hypot — 엔진 구현(스케일링 방식)이 제각각이라 제곱합의 제곱근으로 고정한다(모형 좌표는 넘침 걱정 없는 크기) */
export const hypot = (...a: number[]): number => {
  let s = 0;
  for (const v of a) {
    const n = +v;
    if (n === Infinity || n === -Infinity) return Infinity;
    s += n * n;
  }
  return Math.sqrt(s);
};

/** 한 묶음 — `Math` 자리에 끼워 쓰기 좋게 */
export const detMath = { sin, cos, atan, atan2, acos, hypot } as const;
export type DetMath = typeof detMath;
